import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  type CustomerPublishState,
  customerPublishState,
  formatEditionDate
} from "./education-track-helpers.ts"
import type { EditionView } from "./types/education.types.ts"

const { Text } = Typography

const STATUS_ICON: Record<CustomerPublishState, React.ReactNode> = {
  published: <CheckCircleOutlined aria-hidden />,
  reviewRequired: <ExclamationCircleOutlined aria-hidden />,
  unpublished: <ClockCircleOutlined aria-hidden />
}

interface CustomerPublishStatusProps {
  edition: EditionView
  dateFormat: string
  onPreview: () => void
}

/**
 * Screen 5's header row — "STATUS: Unpublished / Published {date} / Review
 * required" (text + icon) and "Preview as customer". Mounted by
 * `EducationTrackView` inside `EditionHeader`, beside the audio player, for
 * the customer track only — the brief's wireframe puts this row in the
 * header, not the footer (see `CustomerPublishBar`, which owns the footer
 * actions: Facts and sources / Regenerate / Publish / Approve).
 */
export function CustomerPublishStatus({
  edition,
  dateFormat,
  onPreview
}: CustomerPublishStatusProps) {
  const { t } = useTranslation()
  const publishState = customerPublishState(edition)

  const statusText =
    publishState === "published"
      ? t("projectEducation.customerPublishBar.status.publishedOn", {
          date: edition.published_at ? formatEditionDate(edition.published_at, dateFormat) : ""
        })
      : t(`projectEducation.trackView.publishStatus.${publishState}`)

  return (
    <Flex align={"center"} gap={"middle"} wrap data-testid={"customer-publish-status-bar"}>
      <Flex align={"center"} gap={8} data-testid={"customer-publish-status"}>
        <Text type={"secondary"}>{t("projectEducation.customerPublishBar.statusLabel")}</Text>
        <Flex align={"center"} gap={6}>
          {STATUS_ICON[publishState]}
          <Text type={publishState === "reviewRequired" ? "warning" : undefined}>
            {statusText}
          </Text>
        </Flex>
      </Flex>

      <Button onClick={onPreview} data-testid={"preview-as-customer"}>
        {t("projectEducation.customerPublishBar.preview")}
      </Button>
    </Flex>
  )
}
